import React from 'react'
import { Box, Typography } from '@mui/material'
import { CalendarDaysIcon, UsersIcon, VideoCameraIcon } from '@heroicons/react/24/outline'
import type { FacilitatorSprintCallCard } from '../../../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { Button } from '../../../../interaxDesignSystemComponents'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { ControlTowerActionOutcomeAlert } from './ControlTowerActionOutcomeAlert'
import { ControlTowerStatTile } from './ControlTowerStatTile'
import { ControlTowerStatusPill } from './ControlTowerStatusPill'
import { ControlTowerSprintCallRoster } from './ControlTowerSprintCallRoster'
import {
  formatScheduledWindow,
  formatSprintCallPresence,
  getSprintCallKindLabel,
  getSprintCallStatus
} from './controlTowerHelpers'
import {
  buildControlTowerPillSx,
  controlTowerActionRowSx,
  controlTowerActivityTitleSx,
  controlTowerButtonIconStyle,
  controlTowerCardSx,
  controlTowerHeaderRowSx,
  controlTowerKindLabelSx,
  controlTowerMetaItemSx,
  controlTowerMetaRowSx,
  controlTowerMetaIconStyle,
  controlTowerMetaTextSx,
  controlTowerTileIconStyle,
  controlTowerTitleGroupSx,
  controlTowerTitleSx
} from './controlTowerStyles'
import { useJoinSprintCall } from './useJoinSprintCall'

interface ControlTowerSprintCallCardProps {
  sprintCall: FacilitatorSprintCallCard
}

/** An activity every team of the sprint holds together: its schedule, who is and is not in its room, and the way in. */
export const ControlTowerSprintCallCard: React.FC<ControlTowerSprintCallCardProps> = ({ sprintCall }) => {
  const { t } = useTranslation()
  const joinCall = useJoinSprintCall()
  const testIdPrefix = `control-tower-sprint-call-${sprintCall.cardId}`
  const kindLabel = getSprintCallKindLabel(sprintCall, t)
  const status = getSprintCallStatus(sprintCall)
  const scheduledWindow = formatScheduledWindow(sprintCall)
  const presence = formatSprintCallPresence(sprintCall)

  const joinCallLabel = joinCall.isOpening
    ? t('facilitator.controlTower.joinCallOpening', 'Opening…')
    : t('facilitator.controlTower.joinCall', 'Join call')

  return (
    <Box data-testid={testIdPrefix} sx={controlTowerCardSx}>
      <Box sx={controlTowerHeaderRowSx}>
        <Box sx={controlTowerTitleGroupSx}>
          <Box sx={buildControlTowerPillSx(INTERAX_COLORS.heritageBlue)}>
            <Typography sx={controlTowerKindLabelSx}>{kindLabel}</Typography>
          </Box>
          <Typography sx={controlTowerTitleSx} noWrap>
            {t('facilitator.controlTower.allTeams', 'All teams')}
          </Typography>
        </Box>

        <ControlTowerStatusPill status={status} testId={`${testIdPrefix}-status`} />
      </Box>

      <Typography sx={controlTowerActivityTitleSx}>{sprintCall.activityTitle}</Typography>

      <Box sx={controlTowerMetaRowSx}>
        <Box sx={controlTowerMetaItemSx}>
          <CalendarDaysIcon style={controlTowerMetaIconStyle} />
          <Typography sx={controlTowerMetaTextSx}>{scheduledWindow}</Typography>
        </Box>
      </Box>

      <ControlTowerStatTile
        icon={<UsersIcon style={controlTowerTileIconStyle} />}
        iconColor={INTERAX_COLORS.green}
        value={presence}
        label={t('facilitator.controlTower.inMeetingTile', 'In meeting')}
        testId={`${testIdPrefix}-in-meeting`}
      />

      <ControlTowerSprintCallRoster participants={sprintCall.participants} testIdPrefix={testIdPrefix} />

      <ControlTowerActionOutcomeAlert outcome={joinCall.outcome} onClose={joinCall.clearOutcome} />

      <Box sx={controlTowerActionRowSx}>
        <Button
          variant="secondary"
          disabled={joinCall.isOpening}
          onClick={() => joinCall.join(sprintCall)}
          icon={<VideoCameraIcon style={controlTowerButtonIconStyle} />}
          data-testid={`${testIdPrefix}-join-call`}
        >
          {joinCallLabel}
        </Button>
      </Box>
    </Box>
  )
}
